import { useState, useEffect, useCallback, useRef } from 'react';
import { YahooFinanceWebSocketService } from '../services/yahooFinanceWebSocket';
import { fetchHistoricalFromBackend } from '../services/backendWebSocket';
import { StockData } from '../types/stock';
import { filterMarketHoursData, isWithinMarketHours, isSymbol24HourMarket } from '../utils/marketHours';
import { logger } from '../utils/logger';

const INTERVAL_POINT_LIMITS: Record<string, number> = {
  '1m': 20000,
  '1min': 20000,
  '5m': 15000,
  '5min': 15000,
  '15m': 12000,
  '30m': 9000,
  '30min': 9000,
  '1h': 6000,
  '1hour': 6000
};

const MAX_AUTO_HISTORY_PAGES = 3;

// Only crypto pairs should hit the REST /api/klines endpoint.
// Stocks/ETFs (AAPL, SPY, VOO, .IXIC, GC=F, etc.) rely on the stock WebSocket for history.
const isCryptoSymbol = (sym: string) => {
  const s = (sym || '').toUpperCase();
  return /USDT$|USDC$|BTC$|ETH$|BNB$|SOL$|DOGE$/.test(s) || s.includes('/');
};

interface UseYahooFinanceChartDataProps {
  symbol?: string;
  interval?: string;
  period?: string;
  autoStart?: boolean;
}

export const useYahooFinanceChartData = ({
  symbol = 'AAPL',
  interval = '1h',
  period = '3mo',
  autoStart = true
}: UseYahooFinanceChartDataProps) => {
  const REALTIME_INTERVALS = new Set<string>(['1m', '1min']);
  const [data, setData] = useState<StockData[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [latestPrice, setLatestPrice] = useState<number | null>(null);
  const [priceChange, setPriceChange] = useState<{ isPositive: boolean; value: number; percentage: number }>({
    isPositive: true,
    value: 0,
    percentage: 0
  });
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);

  // Chart update tracking
  const [lastUpdateType, setLastUpdateType] = useState<'initial' | 'newCandle' | 'updateCandle'>('initial');
  const [lastUpdatedCandle, setLastUpdatedCandle] = useState<StockData | null>(null);

  const serviceRef = useRef<YahooFinanceWebSocketService | null>(null);
  const intervalRef = useRef(interval);
  const connectionGracePeriodRef = useRef<NodeJS.Timeout | null>(null);
  const errorGracePeriodRef = useRef<NodeJS.Timeout | null>(null);
  const historyCursorRef = useRef<string | null>(null);
  const historyBufferRef = useRef<StockData[]>([]);

  const handleStockUpdate = useCallback((stockData: StockData) => {
    // Only update if this is for the selected symbol
    if (stockData.symbol !== symbol) {
      return;
    }

    // Live WebSocket updates are 1-minute candles; ignore them for higher intervals
    if (!REALTIME_INTERVALS.has(intervalRef.current.toLowerCase())) {
      return;
    }

    if (!isSymbol24HourMarket(symbol) && !isWithinMarketHours(stockData.time)) {
      logger.log(`📊 Skipping ${symbol} update outside market hours:`, new Date(stockData.time * 1000).toLocaleString());
      return;
    }

    // Update latest price for real-time updates
    setLatestPrice(stockData.close);

    // Calculate price change and update chart data
    setData(prevData => {
      if (prevData.length > 0) {
        const previousPrice = prevData[prevData.length - 1]?.close || stockData.close;
        const change = stockData.close - previousPrice;
        const changePercent = (change / previousPrice) * 100;

        setPriceChange({
          isPositive: change >= 0,
          value: Math.abs(change),
          percentage: Math.abs(changePercent)
        });

        // Update the last candlestick with real-time price
        const updatedData = [...prevData];
        if (updatedData.length > 0) {
          const lastCandle = updatedData[updatedData.length - 1];
          const updatedCandle = {
            ...lastCandle,
            close: stockData.close,
            high: Math.max(lastCandle.high, stockData.close),
            low: Math.min(lastCandle.low, stockData.close),
            volume: stockData.volume || lastCandle.volume
          };
          updatedData[updatedData.length - 1] = updatedCandle;

          // Set chart update tracking
          setLastUpdateType('updateCandle');
          setLastUpdatedCandle(updatedCandle);
        }
        return updatedData;
      }

      return prevData;
    });
  }, [symbol]);

  const mergeCandles = useCallback((existing: StockData[], incoming: StockData[]) => {
    if (!incoming.length) {
      return existing;
    }
    const map = new Map<number, StockData>();
    for (const item of existing) {
      map.set(item.time, item);
    }
    for (const item of incoming) {
      map.set(item.time, item);
    }
    return Array.from(map.values()).sort((a, b) => a.time - b.time);
  }, []);

  const applyHistoricalData = useCallback((rawData: StockData[], targetSymbol: string, targetInterval: string) => {
    if (targetSymbol !== symbol || targetInterval !== intervalRef.current) {
      return;
    }

    const intervalKey = targetInterval.toLowerCase();
    const limit = INTERVAL_POINT_LIMITS[intervalKey];
    let processedData: StockData[] = [];

    setData(prevData => {
      const merged = mergeCandles(prevData, rawData);
      const marketHoursData = filterMarketHoursData(merged, targetInterval, targetSymbol);
      processedData = marketHoursData.length > 0 ? marketHoursData : merged.slice(-100);

      if (limit && processedData.length > limit) {
        processedData = processedData.slice(-limit);
      }

      logger.log(
        `📈 Setting chart data: showing ${processedData.length} points for ${targetSymbol} (requested interval ${targetInterval})`
      );
      return processedData;
    });

    setIsLoading(false);
    setLastUpdateType('initial');
    setLastUpdatedCandle(null);

    if (processedData.length > 0) {
      const latest = processedData[processedData.length - 1];
      setLatestPrice(latest.close);

      if (processedData.length > 1) {
        const previous = processedData[processedData.length - 2];
        const change = latest.close - previous.close;
        const changePercent = (change / previous.close) * 100;

        setPriceChange({
          isPositive: change >= 0,
          value: Math.abs(change),
          percentage: Math.abs(changePercent)
        });
      }
    } else {
      setLatestPrice(null);
    }
  }, [symbol, mergeCandles]);

  const loadHistoricalDataFromWebSocket = useCallback((): void => {
    const svc = serviceRef.current;
    if (!svc || !svc.isConnected()) {
      // Avoid setState loops when WS isn't ready yet; just log and return.
      logger.warn(`⚠️ WebSocket not connected, skipping historical request for ${symbol}`);
      return;
    }
    const intervalKey = intervalRef.current || interval;
    logger.log(`📊 Requesting historical data for ${symbol} (${intervalKey}) via WebSocket`);
    svc.requestHistoricalData(symbol, '3mo', intervalKey);
  }, [symbol, interval]);

  const loadHistoricalDataFromBackend = useCallback(
    async (overrideSymbol?: string, overrideInterval?: string) => {
      const targetSymbol = overrideSymbol || symbol;
      const targetInterval = (overrideInterval || intervalRef.current).toLowerCase();

      // For stocks/ETFs, bypass REST (which is crypto-only) and use stock WebSocket history.
      if (!isCryptoSymbol(targetSymbol)) {
        logger.log(`📡 Skipping REST klines for stock symbol ${targetSymbol}; requesting via WebSocket instead`);
        loadHistoricalDataFromWebSocket();
        return;
      }

      try {
        let cursor: string | undefined;
        let pagesLoaded = 0;

        while (true) {
          const { data: pageData, nextCursor } = await fetchHistoricalFromBackend(
            targetSymbol,
            targetInterval,
            5000,
            cursor
          );

          if (pageData && pageData.length) {
            historyBufferRef.current = historyBufferRef.current.length
              ? mergeCandles(historyBufferRef.current, pageData as StockData[])
              : (pageData as StockData[]);
            pagesLoaded += 1;
            applyHistoricalData(historyBufferRef.current, targetSymbol, targetInterval);
          }

          const reachedAutoLimit = pagesLoaded >= MAX_AUTO_HISTORY_PAGES;

          if (!nextCursor) {
            historyCursorRef.current = null;
            setHasMoreHistory(false);
            break;
          }

          historyCursorRef.current = nextCursor;
          setHasMoreHistory(true);

          if (reachedAutoLimit) {
            logger.log(
              `⏸️ Reached auto-pagination limit (${MAX_AUTO_HISTORY_PAGES} pages) for ${targetSymbol}@${targetInterval}, waiting for user action to load more`
            );
            break;
          }

          cursor = nextCursor;
        }

        if (!pagesLoaded && targetSymbol === symbol && targetInterval === intervalRef.current) {
          setData([]);
          setIsLoading(false);
        }
      } catch (err) {
        logger.error('Failed to fetch historical data from backend:', err);
        if (targetSymbol === symbol && targetInterval === intervalRef.current) {
          setError('Failed to load historical data from backend');
          setIsLoading(false);
        }
      }
    },
    [symbol, applyHistoricalData, mergeCandles, loadHistoricalDataFromWebSocket]
  );

  const handleHistoricalData = useCallback((receivedSymbol: string, historicalData: StockData[]) => {
    logger.log(`📊 Received historical data for ${receivedSymbol}: ${historicalData.length} points`);
    if (receivedSymbol === symbol) {
      applyHistoricalData(historicalData, symbol, intervalRef.current);
    }
  }, [symbol, applyHistoricalData]);

  const handleConnectionChange = useCallback((connected: boolean): void => {
    logger.log(`🔗 Yahoo Finance connection changed: ${connected}`);
    setIsConnected(connected);
    if (connected) {
      setError(null);
      // Request historical data after a delay to ensure connection is stable
      setTimeout(() => {
        if (serviceRef.current && serviceRef.current.isConnected()) {
          loadHistoricalDataFromWebSocket();
        } else {
          logger.warn('⚠️ Connection not ready, skipping historical data request');
        }
      }, 2000); // Increased delay for more stability
    } else {
      // If connection fails, try to load data from WebSocket as fallback
      logger.log(`📊 Connection failed, trying to load data from WebSocket for ${symbol}`);
      loadHistoricalDataFromWebSocket();
    }
  }, [symbol, period, interval, loadHistoricalDataFromWebSocket]);

  const handleError = useCallback((errorMessage: string) => {
    // Filter out routine connection messages
    const routineMessages = [
      'WebSocket connection failed',
      'Connection lost',
      'Reconnecting',
      'Failed to connect'
    ];

    const isRoutineError = routineMessages.some(msg => errorMessage.includes(msg));

    if (!isRoutineError) {
      // Add grace period for errors to prevent flashing
      if (errorGracePeriodRef.current) {
        clearTimeout(errorGracePeriodRef.current);
      }

      errorGracePeriodRef.current = setTimeout(() => {
        setError(errorMessage);
      }, 3000);
    }
  }, []);

  const startService = useCallback(async (): Promise<void> => {
    // Prevent multiple service starts
    if (serviceRef.current && serviceRef.current.isConnected()) {
      logger.log('📊 Service already connected, skipping start');
      return;
    }

    if (serviceRef.current) {
      serviceRef.current.disconnect();
    }

    setIsLoading(true);
    setError(null);

    try {
      const service = YahooFinanceWebSocketService.getInstance(
        handleStockUpdate,
        handleConnectionChange,
        handleError,
        handleHistoricalData
      );

      serviceRef.current = service;
      logger.log(`🔧 Service initialized for ${symbol}`);

      service.connect();

      // Process any pending historical data
      service.processPendingHistoricalData();

      // Set a timeout to fallback to API data if connection takes too long
      const connectionTimeout = setTimeout(() => {
        if (!isConnected) {
          logger.log(`⏰ Connection timeout, requesting historical data via WebSocket for ${symbol} (${intervalRef.current})`);
          loadHistoricalDataFromBackend();
        }
      }, 10000); // 10 second timeout

      // Clear timeout if connection succeeds
      const checkConnection = setInterval(() => {
        if (isConnected) {
          clearTimeout(connectionTimeout);
          clearInterval(checkConnection);
        }
      }, 1000);

      // Cleanup timeout after 15 seconds
      setTimeout(() => {
        clearTimeout(connectionTimeout);
        clearInterval(checkConnection);
      }, 15000);

    } catch (err) {
      setError(`Failed to start Yahoo Finance service: ${err}`);
      setIsLoading(false);
    }
  }, [handleStockUpdate, handleConnectionChange, handleError, symbol, period, interval, isConnected, loadHistoricalDataFromWebSocket, loadHistoricalDataFromBackend]);

  const updateSubscription = useCallback((newSymbol: string, newInterval: string) => {
    setIsLoading(true);
    intervalRef.current = newInterval;
    if (serviceRef.current && serviceRef.current.isConnected()) {
      serviceRef.current.requestHistoricalData(newSymbol, period, newInterval);
    }
  }, [period]);

  const loadMoreHistory = useCallback(async () => {
    const cursor = historyCursorRef.current;
    if (!cursor) {
      setHasMoreHistory(false);
      return false;
    }
    setIsLoading(true);
    try {
      const { data: pageData, nextCursor } = await fetchHistoricalFromBackend(
        symbol,
        intervalRef.current,
        5000,
        cursor
      );
      if (pageData && pageData.length) {
        historyBufferRef.current = historyBufferRef.current.length
          ? mergeCandles(historyBufferRef.current, pageData as StockData[])
          : (pageData as StockData[]);
        applyHistoricalData(historyBufferRef.current, symbol, intervalRef.current);
      }
      historyCursorRef.current = nextCursor || null;
      setHasMoreHistory(Boolean(nextCursor));
      return Boolean(pageData && pageData.length);
    } catch (err) {
      logger.error('Failed to load more historical data:', err);
      setError('Failed to load additional historical data');
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [symbol, applyHistoricalData, mergeCandles]);

  // Keep intervalRef in sync
  useEffect(() => {
    intervalRef.current = interval;
  }, [interval]);

  const stopService = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.disconnect();
      serviceRef.current = null;
    }
    setIsConnected(false);
    setError(null);
  }, []);

  // Auto-start service
  useEffect(() => {
    if (autoStart) {
      // Always start the service first, then request data
      logger.log(`📊 Starting service for ${symbol}`);
      logger.log(`DEBUG: handleHistoricalData before getInstance:`, handleHistoricalData);
      startService();
    }

    return () => {
      if (connectionGracePeriodRef.current) {
        clearTimeout(connectionGracePeriodRef.current);
      }
      if (errorGracePeriodRef.current) {
        clearTimeout(errorGracePeriodRef.current);
      }
    };
  }, [autoStart, symbol]);

  // Fetch historical data whenever symbol/interval changes
  useEffect(() => {
    setIsLoading(true);
    setData([]);
    historyCursorRef.current = null;
    historyBufferRef.current = [];
    setHasMoreHistory(false);
    loadHistoricalDataFromBackend();
  }, [symbol, interval, loadHistoricalDataFromBackend]);

  // Update subscription when symbol or interval changes
  useEffect(() => {
    if (serviceRef.current && serviceRef.current.isConnected()) {
      updateSubscription(symbol, interval);
    } else {
      logger.log(`📊 Not connected, loading data from WebSocket for ${symbol}`);
      loadHistoricalDataFromWebSocket();
    }
  }, [symbol, interval, updateSubscription, loadHistoricalDataFromWebSocket]);

  // Update callback when handleHistoricalData changes
  useEffect(() => {
    if (serviceRef.current) {
      logger.log(`🔧 Updating callback for ${symbol}, handleHistoricalData:`, !!handleHistoricalData);
      const service = YahooFinanceWebSocketService.getInstance(
        handleStockUpdate,
        handleConnectionChange,
        handleError,
        handleHistoricalData
      );
      serviceRef.current = service;
    }
  }, [handleHistoricalData, handleStockUpdate, handleConnectionChange, handleError]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopService();
    };
  }, [stopService]);

  return {
    data,
    isConnected,
    latestPrice,
    priceChange,
    error,
    isLoading,
    updateSubscription,
    startService,
    stopService,
    // Chart update tracking
    lastUpdateType,
    lastUpdatedCandle,
    hasMoreHistory,
    loadMoreHistory
  };
};
